/**
 * Problem Reports Admin Page - ClientAdmin and SystemAdmin only
 * View and manage problem reports from users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Loader2, RefreshCw, Filter, Eye,
  ChevronLeft, ChevronRight, X, Clock, User as UserIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { API_ENDPOINTS } from '../../config/api';
import { getToken } from '../../utils/tokenManager';
import {
  ProblemSeverity,
  ProblemCategory,
  ProblemReportStatus,
  SeverityLabels,
  CategoryLabels,
  StatusLabels,
  SeverityColors,
  StatusColors,
  AdminProblemReportResponse,
  ProblemReportsListResponse
} from '../../types/problemReport';
import { getErrorMessage } from '../../services/apiService';

const ProblemReports: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isClientAdmin = user?.role === 'ClientAdmin';
  const isSystemAdmin = user?.isSystemAdmin === true;

  // Redirect if not authorized
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
    } else if (!isClientAdmin && !isSystemAdmin) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, isClientAdmin, isSystemAdmin]);

  const [reports, setReports] = useState<AdminProblemReportResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Pagination
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // View mode tabs
  const [viewMode, setViewMode] = useState<'open' | 'closed' | 'all'>('open');

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProblemReportStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<ProblemSeverity | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<ProblemCategory | ''>('');

  const loadReports = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const params = new URLSearchParams();
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      params.append('viewMode', viewMode);

      if (statusFilter !== '') {
        params.append('status', statusFilter.toString());
      }
      if (severityFilter !== '') {
        params.append('severity', severityFilter.toString());
      }
      if (categoryFilter !== '') {
        params.append('category', categoryFilter.toString());
      }

      const response = await fetch(
        `${API_ENDPOINTS.PROBLEM_REPORTS.ADMIN_LIST}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load problem reports');
      }

      const data: ProblemReportsListResponse = await response.json();
      setReports(data.reports);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [pageNumber, pageSize, viewMode, statusFilter, severityFilter, categoryFilter, navigate]);

  useEffect(() => {
    if (user && (isClientAdmin || isSystemAdmin)) {
      loadReports();
    }
  }, [user, isClientAdmin, isSystemAdmin, loadReports]);

  const clearFilters = () => {
    setStatusFilter('');
    setSeverityFilter('');
    setCategoryFilter('');
    setPageNumber(1);
  };

  const handleViewModeChange = (mode: 'open' | 'closed' | 'all') => {
    setViewMode(mode);
    setStatusFilter('');
    setPageNumber(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasFilters = statusFilter !== '' || severityFilter !== '' || categoryFilter !== '';

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f0]">
      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a]">Problem Reports</h1>
              <p className="text-[#64748b] mt-1">
                {isSystemAdmin ? 'All user-reported issues' : 'Issues from your organization'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  showFilters || hasFilters
                    ? 'bg-[#c41e3a]/10 border-[#c41e3a] text-[#c41e3a]'
                    : 'bg-white border-black/[0.08] text-[#475569] hover:border-[#c41e3a]/50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasFilters && (
                  <span className="w-5 h-5 bg-[#c41e3a] text-white text-xs rounded-full flex items-center justify-center">
                    {[statusFilter, severityFilter, categoryFilter].filter(f => f !== '').length}
                  </span>
                )}
              </button>
              <button
                onClick={loadReports}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-black/[0.08] rounded-lg text-[#475569] hover:border-[#c41e3a]/50 transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* View Mode Tabs */}
          <div className="flex items-center gap-1 mb-6 border-b border-black/[0.08]">
            {([
              { key: 'open', label: 'Open' },
              { key: 'closed', label: 'Closed' },
              { key: 'all', label: 'All' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleViewModeChange(key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  viewMode === key
                    ? 'border-[#c41e3a] text-[#c41e3a]'
                    : 'border-transparent text-[#64748b] hover:text-[#0f172a]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="glass-card rounded-xl p-4 mb-6 border border-black/[0.08]">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value === '' ? '' : Number(e.target.value) as ProblemReportStatus);
                      setPageNumber(1);
                    }}
                    className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                  >
                    <option value="">All Statuses</option>
                    {Object.entries(StatusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Severity</label>
                  <select
                    value={severityFilter}
                    onChange={(e) => {
                      setSeverityFilter(e.target.value === '' ? '' : Number(e.target.value) as ProblemSeverity);
                      setPageNumber(1);
                    }}
                    className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                  >
                    <option value="">All Severities</option>
                    {Object.entries(SeverityLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value === '' ? '' : Number(e.target.value) as ProblemCategory);
                      setPageNumber(1);
                    }}
                    className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                  >
                    <option value="">All Categories</option>
                    {Object.entries(CategoryLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                {hasFilters && (
                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-[#c41e3a] hover:bg-[#c41e3a]/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Reports List */}
          <div className="glass-card rounded-xl border border-black/[0.08] overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-16 text-[#64748b]">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No problem reports found</p>
                <p className="text-sm mt-1">
                  {hasFilters
                    ? 'Try adjusting your filters'
                    : viewMode === 'open'
                      ? 'No open issues — all caught up!'
                      : viewMode === 'closed'
                        ? 'No closed reports yet'
                        : 'No issues have been reported yet'}
                </p>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="hidden md:grid grid-cols-[1fr_115px_90px_110px_140px_80px] gap-4 px-6 py-3 bg-black/[0.02] border-b border-black/[0.06] text-xs font-medium text-[#64748b] uppercase tracking-wider">
                  <div>Description</div>
                  <div>Status</div>
                  <div>Severity</div>
                  <div>Category</div>
                  <div>Submitted</div>
                  <div className="text-right">Action</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-black/[0.06]">
                  {reports.map((report) => (
                    <div
                      key={report.id}
                      className="grid grid-cols-1 md:grid-cols-[1fr_115px_90px_110px_140px_80px] gap-4 px-6 py-4 hover:bg-black/[0.02] transition-colors"
                    >
                      {/* Description & User Info */}
                      <div className="min-w-0">
                        <p className="text-[#0f172a] font-medium truncate mb-1">
                          {report.description.substring(0, 80)}
                          {report.description.length > 80 && '...'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-[#64748b]">
                          <UserIcon className="w-3 h-3" />
                          <span>{report.userName}</span>
                          {report.clientName && (
                            <>
                              <span className="text-black/20">|</span>
                              <span>{report.clientName}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${StatusColors[report.status]}`}>
                          {StatusLabels[report.status]}
                        </span>
                      </div>

                      {/* Severity Badge */}
                      <div className="flex items-center">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${SeverityColors[report.severity]}`}>
                          {SeverityLabels[report.severity]}
                        </span>
                      </div>

                      {/* Category */}
                      <div className="flex items-center text-sm text-[#475569]">
                        {CategoryLabels[report.category]}
                      </div>

                      {/* Date */}
                      <div className="flex items-center text-sm text-[#64748b]">
                        <Clock className="w-3 h-3 mr-1.5" />
                        {formatDate(report.createdAt)}
                      </div>

                      {/* Action */}
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => navigate(`/admin/problem-reports/${report.id}`)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#c41e3a] hover:bg-[#c41e3a]/10 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-black/[0.06] bg-black/[0.01]">
                    <div className="text-sm text-[#64748b]">
                      Showing {((pageNumber - 1) * pageSize) + 1} to {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} reports
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                        disabled={pageNumber === 1}
                        className="p-2 rounded-lg border border-black/[0.08] hover:bg-black/[0.02] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-[#475569] px-3">
                        Page {pageNumber} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                        disabled={pageNumber === totalPages}
                        className="p-2 rounded-lg border border-black/[0.08] hover:bg-black/[0.02] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProblemReports;
